import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useDelayedEventCheck from '../../../hooks/useDelayedEventCheck';
import TrustpilotWidget from './TrustpilotWidget';

export type TrustpilotPopUpProps = {
  open: boolean;
};

const HAS_CLICKED_IN_14_DAYS_KEY = 'tpClk';
const DELAY_FOR_RETRY_MS = 1000 * 60 * 60 * 24 * 7; // 7 days later
const DELAY_FOR_DO_NOT_ASK_MS = 1000 * 60 * 60 * 24 * 365; // 1 year later

const TrustpilotPopUp: React.FC<TrustpilotPopUpProps> = ({ open }) => {
  const tag = `[${TrustpilotPopUp.name}]`;
  const isTrustPilotLoaded = useRef(
    'Trustpilot' in window
      && typeof window.Trustpilot === 'object'
      && 'loadFromElement' in window.Trustpilot
      && typeof window.Trustpilot.loadFromElement === 'function',
  );

  const { setDelay, shouldShow } = useDelayedEventCheck(
    HAS_CLICKED_IN_14_DAYS_KEY,
    DELAY_FOR_RETRY_MS,
  );

  const [isOpen, setIsOpen] = React.useState(open && shouldShow && isTrustPilotLoaded.current);
  const { t } = useTranslation();

  useEffect(() => {
    setIsOpen(open && shouldShow && isTrustPilotLoaded.current);
    if (!isTrustPilotLoaded.current) {
      console.warn(tag, 'Trustpilot not loaded!');
      setIsOpen(false);
    }
  }, [open, shouldShow]);

  const handleOnCancel = React.useCallback(() => {
    setIsOpen(false);
    setDelay();
  }, [setDelay]);

  const handleOnNotAskAgain = React.useCallback(() => {
    setDelay(DELAY_FOR_DO_NOT_ASK_MS);
    setIsOpen(false);
  }, [setDelay]);

  return (
    <Dialog open={isOpen} onClose={handleOnCancel}>
      <DialogTitle>{t('file.rating.trustpilot.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" marginBottom="1em">
          {t('file.rating.trustpilot.text')}
        </Typography>
        <TrustpilotWidget />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button size="small" onClick={handleOnNotAskAgain}>
          {t('file.rating.trustpilot.not_ask_again')}
        </Button>
        <Button variant="outlined" size="small" onClick={handleOnCancel}>
          {t('file.rating.trustpilot.not_now')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrustpilotPopUp;
